import _extends from "@babel/runtime/helpers/extends";
var _StyledIconContainer, _StyledIconContainer2;
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { animated, useTransition } from '@react-spring/web';
import { INACTIVE_GRAY } from '../colors';
import { ICON_LARGE_SIZE } from '../constants';
import IconButtonLoader from '@tourlane/iconography/Glyphs/Other/ButtonLoader';
import IconError from '@tourlane/iconography/Glyphs/Other/Error';
import SvgIcon from '../SvgIcon';
import Button from './Button';
import Error from '../Error';
import ButtonContainer from './components/ButtonContainer';
import { BUTTON_RESET_WITH_ERROR_TIME } from './constants';
import { fullSpaceAbsoluteStyle } from '../tokens/full-space';
import { absoluteCenteredStyle } from '../tokens/centered';
const SPRING_CONFIG = {
  duration: 250
};
const STATUS = {
  INITIAL: 'INITIAL',
  LOADING: 'LOADING',
  FAILED: 'FAILED'
};
const StyledButton = styled(Button)`
  position: relative;
`;
const StyledHiddenText = styled.span`
  opacity: 0;
`;
const StyledTextContainer = styled.span`
  width: 100%;
  ${absoluteCenteredStyle}
`;
const StyledIconContainer = styled(SvgIcon)`
  ${absoluteCenteredStyle}
`;
const StyledWrapper = styled(animated.span)`
  ${fullSpaceAbsoluteStyle}
`;
const ButtonWithLoader = /*#__PURE__*/React.forwardRef(({
  as,
  forwardedAs,
  fullWidth = false,
  isLoading = false,
  hasError = false,
  error,
  disabled = false,
  children,
  onClick,
  resetTime = BUTTON_RESET_WITH_ERROR_TIME,
  withAbsoluteError = true,
  withNoPaddingForError = false,
  className,
  ...props
}, forwardedRef) => {
  var _StyledTextContainer;
  const [status, setStatus] = useState(STATUS.INITIAL);
  const timeoutResettingId = useRef(null);
  useEffect(() => {
    if (isLoading) {
      clearTimeout(timeoutResettingId.current);
      setStatus(STATUS.LOADING);
    } else {
      if (hasError) {
        setStatus(STATUS.FAILED);
        timeoutResettingId.current = setTimeout(() => setStatus(STATUS.INITIAL), resetTime);
      } else {
        setStatus(STATUS.INITIAL);
      }
    }
    return () => clearTimeout(timeoutResettingId.current);
  }, [isLoading]);
  const isButtonDisabled = status === STATUS.LOADING || status === STATUS.FAILED || disabled;
  const transitions = useTransition(status, {
    config: SPRING_CONFIG,
    keys: null,
    from: {
      opacity: 0,
      transform: 'scale(0.5)'
    },
    enter: {
      opacity: 1,
      transform: 'scale(1)'
    },
    leave: {
      opacity: 0,
      transform: 'scale(1.5)'
    },
    initial: null
  });
  return /*#__PURE__*/React.createElement(ButtonContainer, {
    className: className,
    fullWidth: fullWidth
  }, /*#__PURE__*/React.createElement(StyledButton, _extends({
    ref: forwardedRef,
    noAnimate: !disabled,
    disabled: isButtonDisabled,
    onClick: onClick,
    fullWidth: fullWidth,
    forwardedAs: as ?? forwardedAs
  }, props), transitions((transitionStyle, state) => /*#__PURE__*/React.createElement(StyledWrapper, {
    style: transitionStyle
  }, state === STATUS.LOADING ? _StyledIconContainer || (_StyledIconContainer = /*#__PURE__*/React.createElement(StyledIconContainer, {
    size: ICON_LARGE_SIZE,
    color: INACTIVE_GRAY
  }, /*#__PURE__*/React.createElement(IconButtonLoader, null))) : state === STATUS.FAILED ? _StyledIconContainer2 || (_StyledIconContainer2 = /*#__PURE__*/React.createElement(StyledIconContainer, {
    size: ICON_LARGE_SIZE,
    color: INACTIVE_GRAY
  }, /*#__PURE__*/React.createElement(IconError, null))) : state === STATUS.INITIAL ? _StyledTextContainer || (_StyledTextContainer = /*#__PURE__*/React.createElement(StyledTextContainer, null, children)) : null)), /*#__PURE__*/React.createElement(StyledHiddenText, null, children)), /*#__PURE__*/React.createElement(Error, {
    show: status === STATUS.FAILED && !!error,
    error: error,
    absolute: withAbsoluteError,
    noPadding: withNoPaddingForError
  }));
});
export default ButtonWithLoader;